import React, { useEffect, useRef } from 'react'
import { useMarketStore } from '../../../services/store'
import { getFieldValue } from '../../../config/columns'

interface LiveGlassCellProps {
    symbol: string
    strike: number
    side: 'ce' | 'pe'
    dataKey: 'ltp' | 'changePercent' | 'oiChange' | 'vol' | 'oi'
    chgRefKey: 'ltp' | 'changePercent' | 'oiChange' | 'vol' | 'oi'
    max: number
    isPercent: boolean
    align: 'left' | 'right'
    showBar: boolean
    initialVal: number
    initialChgRef: number
    subText?: React.ReactNode
}

const POS_CLASSES = 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-emerald-300/60 dark:ring-emerald-700/40 text-emerald-700 dark:text-emerald-400 shadow-[0_2px_8px_rgba(16,185,129,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(16,185,129,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(16,185,129,0.15)] hover:-translate-y-px'
const NEG_CLASSES = 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-rose-300/60 dark:ring-rose-700/40 text-rose-700 dark:text-rose-400 shadow-[0_2px_8px_rgba(244,63,94,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(244,63,94,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(244,63,94,0.15)] hover:-translate-y-px'
const NEU_CLASSES = 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-slate-300/60 dark:ring-slate-700/40 text-slate-500 dark:text-slate-400 shadow-[0_2px_8px_rgba(148,163,184,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:-translate-y-px'

export const LiveGlassCell: React.FC<LiveGlassCellProps> = ({
    symbol,
    strike,
    side,
    dataKey,
    chgRefKey,
    max,
    isPercent,
    align,
    showBar,
    initialVal,
    initialChgRef,
    subText
}) => {
    const textRef = useRef<HTMLSpanElement>(null)
    const arrowRef = useRef<HTMLSpanElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const barRef = useRef<HTMLDivElement>(null)
    const currentChgRef = useRef(initialChgRef)
    const prevValRef = useRef(initialVal)

    useEffect(() => {
        let prevChainRef: any = null
        const unsubscribe = useMarketStore.subscribe(
            (state: any) => {
                const chain = state.data[symbol]
                if (!chain || chain === prevChainRef) return
                prevChainRef = chain
                const row = chain.find((r: any) => Number(r.strike) === Number(strike))
                if (!row) return

                const val = row[side]?.[dataKey] || 0
                const chgRef = row[side]?.[chgRefKey] || 0

                // 1. Update Text
                if (textRef.current) {
                    const formatted = isPercent ? `${val.toFixed(1)}%` : val.toFixed(2)
                    const prefix = isPercent && chgRef > 0 ? '+' : ''
                    textRef.current.textContent = `${prefix}${formatted}`
                }

                // 2. Update Arrow
                if (arrowRef.current && isPercent) {
                    arrowRef.current.textContent = chgRef > 0 ? '▲' : chgRef < 0 ? '▼' : '—'
                }

                // 3. Update Bar
                if (barRef.current && max > 0) {
                    const pct = Math.min(Math.max((Math.abs(val) / max) * 100, 0), 100)
                    barRef.current.style.width = `${pct}%`
                }

                // 4. Update Classes if sign flipped
                const wasPos = currentChgRef.current > 0
                const wasNeg = currentChgRef.current < 0
                const isPos = chgRef > 0
                const isNeg = chgRef < 0

                if (isPos !== wasPos || isNeg !== wasNeg) {
                    currentChgRef.current = chgRef
                    if (containerRef.current) {
                        // Remove old
                        const oldClasses = wasPos ? POS_CLASSES : wasNeg ? NEG_CLASSES : NEU_CLASSES
                        oldClasses.split(' ').forEach(c => containerRef.current!.classList.remove(c))
                        // Add new
                        const newClasses = isPos ? POS_CLASSES : isNeg ? NEG_CLASSES : NEU_CLASSES
                        newClasses.split(' ').forEach(c => containerRef.current!.classList.add(c))
                    }
                }

                // 5. Visual Real-Time Tick Flash Highlight (400ms)
                if (prevValRef.current !== null && prevValRef.current !== val && prevValRef.current > 0 && val > 0) {
                    if (containerRef.current) {
                        const isIncrease = val > prevValRef.current
                        const flashClass = isIncrease 
                            ? 'ring-2 ring-emerald-400 bg-emerald-500/30' 
                            : 'ring-2 ring-rose-400 bg-rose-500/30'
                        const classes = flashClass.split(' ')
                        classes.forEach(c => containerRef.current!.classList.add(c))
                        setTimeout(() => {
                            classes.forEach(c => containerRef.current?.classList.remove(c))
                        }, 400)
                    }
                }
                prevValRef.current = val
            }
        )
        return () => unsubscribe()
    }, [symbol, strike, side, dataKey, chgRefKey, max, isPercent])

    const isPos = initialChgRef > 0
    const isNeg = initialChgRef < 0
    const initialClasses = isPos ? POS_CLASSES : isNeg ? NEG_CLASSES : NEU_CLASSES
    const formattedInitial = isPercent ? `${initialVal.toFixed(1)}%` : initialVal.toFixed(2)

    // Bar logic
    const pct = max > 0 ? (Math.abs(initialVal) / max) * 100 : 0
    const color = initialChgRef > 0 ? 'emerald' : 'rose'
    let beamGradient = 'from-slate-200 to-slate-150 dark:from-slate-700 dark:to-slate-750'
    if (color === 'emerald') {
        beamGradient = align === 'left'
            ? 'bg-linear-to-r from-emerald-400/80 via-emerald-300/70 to-emerald-200/60 dark:from-emerald-500/60 dark:via-emerald-600/50 dark:to-emerald-700/40'
            : 'bg-linear-to-l from-emerald-400/80 via-emerald-300/70 to-emerald-200/60 dark:from-emerald-500/60 dark:via-emerald-600/50 dark:to-emerald-700/40'
    } else if (color === 'rose') {
        beamGradient = align === 'left'
            ? 'bg-linear-to-r from-rose-400/80 via-rose-300/70 to-rose-200/60 dark:from-rose-500/60 dark:via-rose-600/50 dark:to-rose-700/40'
            : 'bg-linear-to-l from-rose-400/80 via-rose-300/70 to-rose-200/60 dark:from-rose-500/60 dark:via-rose-600/50 dark:to-rose-700/40'
    }

    return (
        <div className={`flex flex-col justify-center h-full ${align === 'right' ? 'items-end' : 'items-start'}`}>
            <div className={`flex items-center gap-1 mb-1 ${align === 'right' ? 'ml-auto' : 'mr-auto'}`}>
                {subText && align === 'right' && subText}
                <div 
                    ref={containerRef}
                    className={`relative inline-flex w-fit min-w-[70px] px-2 h-[30px] items-center justify-center rounded-md text-[13px] font-black tabular-nums tracking-tight transition-all duration-300 ${initialClasses}`}
                >
                    {isPercent && <span ref={arrowRef} className="text-[10px] mr-1 opacity-80">{isPos ? '▲' : isNeg ? '▼' : '—'}</span>}
                    <span ref={textRef}>
                        {isPercent && isPos ? '+' : ''}{formattedInitial}
                    </span>
                </div>
                {subText && align === 'left' && subText}
            </div>
            {showBar && (
                <div className="w-full max-w-[80px] mt-1">
                    <div className={`flex flex-col justify-center min-w-[60px] ${align === 'right' ? 'items-end' : 'items-start'}`}>
                        <div className={`h-[3px] w-full bg-slate-100/60 dark:bg-slate-800/30 rounded-full relative flex ${align === 'right' ? 'justify-end' : 'justify-start'} overflow-hidden`}>
                            <div
                                ref={barRef}
                                className={`h-full rounded-full relative transition-all duration-500 ease-out ${beamGradient}`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
